// original mobile ready
import React, { useState, useEffect } from 'react';
import {
    Alert,
    Drawer,
    AppBar,
    CircularProgress,
    Toolbar,
    Tooltip,
    Typography,
    IconButton,
    Box,
    Tabs,
    Tab,
    Grid,
    Card,
    CardContent,
    CardActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    Stack,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Avatar,
    Divider,
    Container,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';

import { createTheme, ThemeProvider, styled } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import SpaOutlinedIcon from '@mui/icons-material/SpaOutlined';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import BarChartIcon from '@mui/icons-material/BarChart';
import LogoutIcon from '@mui/icons-material/Logout';
import { useHistory } from "react-router-dom";
import axios from 'axios';
import Salons from './Salons';
import Sprovider from './Sprovider';
import LocationPicker from './LocationPicker';
import Geolocation from './GeoLocation';
import Geoaddress from './Geoaddress';

// Custom Theme
const theme = createTheme({
    palette: {
        primary: { main: '#6a1b9a' },
        secondary: { main: '#ab47bc' },
    },
    typography: {
        h3: {
            fontSize: '2.5rem',
            '@media (max-width:600px)': {
                fontSize: '1.8rem',
            },
        },
        h5: {
            fontSize: '1.5rem',
            '@media (max-width:600px)': {
                fontSize: '1.2rem',
            },
        },
        h6: {
            fontSize: '1.25rem',
            '@media (max-width:600px)': {
                fontSize: '1rem',
            },
        },
    },
});

// Styled Components
const StyledAppBar = styled(AppBar)(({ theme }) => ({
    background: 'linear-gradient(45deg, #6a1b9a 30%, #ab47bc 90%)',
    boxShadow: '0 3px 10px rgba(0,0,0,0.2)',
    [theme.breakpoints.up('md')]: {
        width: '100%',
    },
    [theme.breakpoints.down('md')]: {
        width: '100%',
    },
}));

const StyledDrawer = styled(Drawer)(({ theme }) => ({
    '& .MuiDrawer-paper': {
        background: 'linear-gradient(180deg,rgb(60, 96, 174) 0%,rgb(190, 208, 231) 50%)',
        boxSizing: 'border-box',
        width: 250,
        boxShadow: '0 0 10px rgba(0,0,0,0.1)',
        [theme.breakpoints.down('sm')]: {
            width: '100%',
            maxWidth: 250,
        },
    },
}));

const StyledTab = styled(Tab)(({ theme }) => ({
    color: theme.palette.grey[800],
    '&.Mui-selected': {
        color: theme.palette.primary.main,
        backgroundColor: theme.palette.grey[100],
    },
    '&:hover': {
        backgroundColor: theme.palette.grey[200],
        color: theme.palette.primary.main,
    },
    [theme.breakpoints.down('sm')]: {
        fontSize: '0.9rem',
    },
}));

const DashboardContainer = styled(Box)(({ theme }) => ({
    minHeight: '100vh',
    background: 'white',
    padding: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(2),
    },
}));

const HeroSection = styled(Box)(({ theme }) => ({
    width: '100%',
    height: '310px',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    textAlign: 'center',
    '&:before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
    },
    [theme.breakpoints.down('sm')]: {
        height: '200px',
    },
}));

const GradientTypography = styled(Typography)(({ theme }) => ({
    background: 'linear-gradient(45deg, #6a1b9a 30%, #ab47bc 90%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    fontWeight: 'bold',
}));

const StyledCard = styled(Card)(({ theme }) => ({
    border: '2px solid transparent',
    backgroundImage: 'linear-gradient(white, white), linear-gradient(45deg, #6a1b9a, #ab47bc)',
    backgroundOrigin: 'border-box',
    backgroundClip: 'padding-box, border-box',
    borderRadius: theme.spacing(2),
    boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
    transition: 'transform 0.3s ease',
    '&:hover': {
        transform: 'scale(1.02)',
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.grey[100],
    },
    '&:hover': {
        backgroundColor: theme.palette.grey[200],
        cursor: 'pointer',
    },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    fontWeight: 500,
    color: theme.palette.text.primary,
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(1),
        fontSize: '0.8rem',
    },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: theme.spacing(1),
        '&:hover fieldset': {
            borderColor: theme.palette.primary.main,
        },
        '&.Mui-focused fieldset': {
            borderColor: theme.palette.primary.main,
        },
    },
    '& .MuiInputLabel-root': {
        color: theme.palette.grey[600],
        '&.Mui-focused': {
            color: theme.palette.primary.main,
        },
    },
    [theme.breakpoints.down('sm')]: {
        '& .MuiInputBase-input': {
            fontSize: '0.9rem',
        },
        '& .MuiInputLabel-root': {
            fontSize: '0.9rem',
        },
    },
}));

const StyledButton = styled(Button)(({ theme }) => ({
    background: 'linear-gradient(45deg, #6a1b9a 30%, #ab47bc 90%)',
    color: 'white',
    padding: theme.spacing(1.5),
    borderRadius: theme.spacing(1),
    textTransform: 'none',
    fontWeight: 'bold',
    '&:hover': {
        background: 'linear-gradient(45deg, #4a0072 30%, #8e24aa 90%)',
        transform: 'scale(1.02)',
        transition: 'all 0.3s ease',
    },
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(1),
        fontSize: '0.9rem',
    },
}));

const bgUrl = `${process.env.PUBLIC_URL}/assets/bg1.jpg`;

const formatTimeForInput = (isoString) => {
    if (!isoString) return '';
    if (isoString.includes(':')) {
        const [hours, minutes] = isoString.split(':');
        if (hours && minutes && !isNaN(hours) && !isNaN(minutes)) {
            return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
        }
    }
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return '';
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
};

const formatTimeForBackend = (timeString) => {
    if (!timeString) return null;
    const [hours, minutes] = timeString.split(':');
    const date = new Date();
    date.setHours(parseInt(hours, 10));
    date.setMinutes(parseInt(minutes, 10));
    date.setSeconds(0);
    date.setMilliseconds(0);
    const offset = 5.5 * 60;
    const isoString = new Date(date.getTime() - (date.getTimezoneOffset() * 60000) + (offset * 60000)).toISOString();
    return isoString.replace('Z', '+05:30');
};

function Dashboard(props) {
    const { spId, setSpId, setSpLoggedIn, setClient, isLoaded } = props;
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [tabValue, setTabValue] = useState(0);
    const [salonFormOpen, setSalonFormOpen] = useState(false);
    const [serviceFormOpen, setServiceFormOpen] = useState(false);
    const [salonData, setSalonData] = useState();
    const [services, setServices] = useState([]);
    const [appointments, setAppointments] = useState([]);
    const [profile, setProfile] = useState();
    const [analytics, setAnalytics] = useState({
        totalAppointments: 50,
        revenueThisMonth: 25000,
        mostPopularService: 'Haircut',
    });
    const [salonForm, setSalonForm] = useState();
    const [serviceForm, setServiceForm] = useState({ id: null, stype: '', sprice: '', sduration: '' });
    const [profileForm, setProfileForm] = useState({
        email: '',
        first_name: '',
        last_name: '',
        date_of_birth: '',
        gender: '',
        mobile: '',
    });
    const [apptTabValue, setApptTabValue] = useState(0);
    const [showServiceProvider, setShowServiceProvider] = useState(false);
    const [showSalon, setShowSalon] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const [image, setImage] = useState('');
    const [latitude, setLatitude] = useState(null);
    const [longitude, setLongitude] = useState(null);

    const spSalonId = localStorage.getItem('spSalonId');
    const [owner, setOwner] = useState(null);
    const { visible, setVisible } = props;

    // const [showServiceModal, setShowServiceModal] = useState(false);
    const [openServiceModal, setOpenServiceModal] = useState(false);
    const [openPaymentModal, setOpenPaymentModal] = useState(false);
    const [subscription, setSubscription] = useState(null);
    const [paymentStatus, setPaymentStatus] = useState(null); // 'success' | 'failed'
    const [isProcessing, setIsProcessing] = useState(false);

    // console.log('spSalonId from localStorage: client', spSalonId);

    const MAX_SIZE_MB = 10;
    const onImageChange = (event) => {
        const file = event.target.files[0];
        if (file && file.size > MAX_SIZE_MB * 1024 * 1024) {
            alert(`File size is ${file.size / (1024 * 1024)} MB. Maximum allowed size is 1 MB. You may reduce the image size using any online image compressor and try again later and upload it going to Edit Salon details`);
            return;
        } else {
            setImage(file);
        }
    };

    const serviceAddUrl = process.env.REACT_APP_SERVER
        ? `https://salonserver-k8m8.onrender.com/services/`
        : `http://localhost:3001/services/`;
    const serviceDelUrl = process.env.REACT_APP_SERVER
        ? `https://salonserver-k8m8.onrender.com/services/`
        : `http://localhost:3001/services/`;
    const serviceEditUrl = process.env.REACT_APP_SERVER
        ? `https://salonserver-k8m8.onrender.com/services/`
        : `http://localhost:3001/services/`;

    const servicesUrl = process.env.REACT_APP_SERVER
        ? `https://salonserver-k8m8.onrender.com/services/${spSalonId}`
        : `http://localhost:3001/services/${spSalonId}`;
    const salonUrl = process.env.REACT_APP_SERVER
        ? `https://salonserver-k8m8.onrender.com/salons/${spSalonId}`
        : `http://localhost:3001/salons/${spSalonId}`;
    const apptsUrl = process.env.REACT_APP_SERVER
        ? `https://salonserver-k8m8.onrender.com/appointments/`
        : `http://localhost:3001/appointments/all`;
    const logoutUrl = process.env.REACT_APP_SERVER
        ? `https://salonserver-k8m8.onrender.com/logout`
        : `http://localhost:3001/logout`;
    const paymentsUrl = process.env.REACT_APP_SERVER
        ? `https://salonserver-k8m8.onrender.com/api/v1/payments`
        : `http://localhost:3001/api/v1/payments`;

    const history = useHistory();

    // useEffect(() => {
    //     axios.get('/api/v1/subscription', {
    //         headers: {
    //             Authorization: localStorage.getItem('token')
    //         }
    //     }).then(res => {
    //         console.log('subscription data', res.data);
    //         setSubscription(res.data.subscription);
    //     });
    // }, []);

    useEffect(() => {
        setVisible(false);
        setClient(false);
        const storedLoggedIn = localStorage.getItem("spLoggedIn");
        const savedOwner = localStorage.getItem("spOwner");


        if (storedLoggedIn === "true" && savedOwner) {
            setSpLoggedIn(true);
            setOwner(JSON.parse(savedOwner));
            // console.log('sp..owner, ...owner', savedOwner, owner)
        }
    }, []);

    // useEffect(() => {
    //     const savedOwner = JSON.parse(localStorage.getItem('spOwner'));
    //     if (savedOwner) {
    //         setOwner(savedOwner);
    //     }
    //     setSpLoggedIn(true);
    //     // setUser(JSON.parse(savedUser));
    // }
    // }, []);

    const getServices = () => {
        if (!(spSalonId > 0)) {
            // console.log("spSalonId is invalid, skipping API call.");
            return;
        }
        axios.get(servicesUrl, {
            headers: {
                Authorization: ` ${localStorage.getItem('token')} `,
            },
        }).then(response => {
            setServices(response.data);
            setServiceForm(response.data);
            // console.log("Services data fetched:", response.data)
            if (response.data.length === 0) {
                // console.log('length 0')
                setOpenServiceModal(true); // 👈 Show modal
                // setTabValue(2);           // 👈 Switch to Services tab
            }
        });

    };

    const getSubscription = () => {
        axios.get(paymentsUrl, {
            headers: {
                Authorization: ` ${localStorage.getItem('token')} `,
            },
        }).then(res => {
            console.log('subscription data', res.data);
            setSubscription(res.data.subscription);
            if (!res.data.subscription) {
                setSubscription(false);
            }
        });
    };

    useEffect(() => {
        // const savedOwner = JSON.parse(localStorage.getItem('spOwner'));
        // if (savedOwner) {
        //     setOwner(savedOwner);
        // }
        // console.log('Retrieved from localStorage:', owner);
        // console.log('owner and salonId', owner, spSalonId, savedOwner?.salon_id);

        const getUserSalon = async () => {
            if (!(spSalonId > 0)) {
                // console.log("spSalonId is invalid, skipping API call.");
                return;
            }
            try {
                const response = await axios.get(salonUrl, {
                    headers: {
                        Authorization: ` ${localStorage.getItem('token')} `,
                    },
                });
                setSalonData(response.data.data);
                setSalonForm(response.data.data);
                // console.log("Salon data fetched:", response.data.data);
            } catch (err) {
                setSalonData(null);
            }
        };



        const fetchAppointments = async () => {
            if (!(spSalonId > 0)) {
                // console.log("spSalonId is invalid, skipping API call.");
                return;
            }
            try {
                const response = await axios.get(apptsUrl, {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: ` ${localStorage.getItem('token')} `,
                    },
                });
                setAppointments(response.data);
            } catch (error) {
                console.error('Error fetching appointments:', error.response ? error.response.data : error.message);
            }
        };

        const setOwnerData = async () => {
            const savedOwner = JSON.parse(localStorage.getItem('spOwner'));
            // console.log('savedOwner:', savedOwner);
            if (savedOwner) {
                setProfile(savedOwner);
                setProfileForm(savedOwner);
                setOwner(savedOwner);
            }
        };

        getUserSalon();
        // console.log('salon form', salonForm);
        getServices();
        fetchAppointments();
        setOwnerData();
        getSubscription();
    }, [spSalonId, services.length]);


    const handleDeleteService = async (id) => {
        const confirmed = window.confirm("Are you sure you want to delete this service?");
        if (!confirmed) return;

        try {
            const response = await axios.delete(
                serviceDelUrl + id,
                {
                    headers: {
                        Authorization: ` ${localStorage.getItem('token')} `,
                    },
                }
            );
            getServices();
        } catch (err) {
            alert(err.message);
        }
    };

    const handleDrawerToggle = () => {
        setDrawerOpen(!drawerOpen);
    };

    // const getServices = () => {
    //     axios.get(servicesUrl, {
    //         headers: {
    //             Authorization: ` ${localStorage.getItem('token')} `,
    //         },
    //     }).then(response => {
    //         setServices(response.data);
    //     });
    // };

    const handleServiceSubmit = (e) => {
        e.preventDefault();
        const service = {
            stype: serviceForm.stype,
            sduration: serviceForm.sduration,
            sprice: serviceForm.sprice,
            salon_id: spSalonId,
        };
        // console.log('sdat', service);
        // console.log('url', serviceAddUrl);
        if (serviceForm.id) {
            // Update existing service
            axios.patch(`${serviceAddUrl}${serviceForm.id}`, service, {
                headers: {
                    Authorization: ` ${localStorage.getItem('token')} `,
                },
            })
                .then(response => {
                    if (response.status === 200) {
                        // console.log('Service Updated');
                        alert('Service Updated Successfully');
                        setServiceFormOpen(false);
                        getServices();
                    }
                });
            return;
        }
        // Add new service
        axios.post(serviceAddUrl, service, {
            headers: {
                Authorization: ` ${localStorage.getItem('token')} `,
            },
        })
            .then(response => {
                if (response.status === 201) {
                    // console.log('Service Added');
                    alert('Service Added Successfully');
                    setServiceFormOpen(false);
                    getServices();
                }
            });
    };

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
        setDrawerOpen(false);
    };

    const handleSalonFormChange = (e) => {
        const { name, value } = e.target;
        if (name === 'opens' || name === 'closes') {
            setSalonForm({ ...salonForm, [name]: value });
        } else {
            setSalonForm({ ...salonForm, [name]: value });
        }
    };

    const handleServiceFormChange = (e) => {
        const { name, value } = e.target;
        setServiceForm({ ...serviceForm, [name]: value });
    };

    const handleProfileFormChange = (e) => {
        const { name, value } = e.target;
        setProfileForm({ ...profileForm, [name]: value });
    };

    const updateSalonDetails = async (event) => {
        event.preventDefault();
        const formData = new FormData();

        formData.append('name', salonForm.name);
        formData.append('email', salonForm.email);
        formData.append('address1', salonForm.address1);
        formData.append('address2', salonForm.address2);
        formData.append('pincode', salonForm.pincode);
        formData.append('city', salonForm.city);
        formData.append('landline', salonForm.landline);
        formData.append('mobile', salonForm.mobile);
        formData.append('gstin', salonForm.gstin);
        formData.append('pan', salonForm.pan);
        formData.append('chairs', salonForm.chairs);
        formData.append('opens', salonForm.opens);
        formData.append('closes', salonForm.closes);
        formData.append('weekday', salonForm.weekday);
        formData.append('latitude', latitude);
        formData.append('longitude', longitude);

        if (image) formData.append('image', image);
        setSubmitting(true);
        try {
            const response = await axios.patch(salonUrl, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Authorization: ` ${localStorage.getItem('token')} `,
                },
            });
            setSalonData(response.data);
            setSalonForm(response.data);
            // console.log("Salon Details updated:", response.data);
            alert('Salon Details updated successfully');
            setTabValue(0);
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to update salon details.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleEditService = (service) => {
        const mappedService = {
            id: service.id,
            stype: service.stype || '',
            sprice: String(service.sprice || ''),
            sduration: service.sduration || '',
        };
        setServiceForm(mappedService);
        setServiceFormOpen(true);
    };

    const profileEditUrl = process.env.REACT_APP_SERVER
        ? `https://salonserver-k8m8.onrender.com/users/${profileForm.id}`
        : `http://localhost:3001/users/${profileForm.id}`;

    const handleProfileSubmit = async (e) => {
        e.preventDefault();
        const updatedProfileData = { ...profileForm };
        try {
            const token = localStorage.getItem('token');

            const response = await axios.put(
                `${profileEditUrl}`,
                updatedProfileData,
                {
                    headers: { Authorization: localStorage.getItem('token') },
                }
            );
            setProfile(response.data);

            // console.log("Profile updated successfully");
            alert('Profile updated successfully');
        } catch (err) {
            console.error('Failed to update profile:', err);
        }
    };

    const handleSpLogout = () => {
        // console.log('Logging out...');
        setClient(true);
        setSpLoggedIn(false);
        setSpId(null);

        localStorage.removeItem('spLoggedIn');
        localStorage.removeItem('spSalonId');
        localStorage.removeItem('spOwner');
        localStorage.removeItem('token');
        // localStorage.clear();

        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            // console.log('Remaining localStorage item:', key);
            // console.log('g', key, localStorage.getItem(key));
            // console.log('length', localStorage.length, i);
        }

        history.push('/');


        // console.log('123');
        // setSpLoggedIn(false);
        // console.log('2')
        // setClient(true);
        // console.log('3')
        // axios.delete(logoutUrl, {
        //     headers: {
        //         'Content-Type': 'application/json',
        //         'Accept': 'application/json',
        //         Authorization: `${localStorage.getItem('token')}`,
        //     },
        // })
        //     .then((response) => {
        //         setSpLoggedIn(false);
        //         setClient(true);
        //         alert(response.data.message);
        //         localStorage.removeItem("token");
        //         localStorage.removeItem("spsalonId");
        //         localStorage.removeItem("owner");
        //         console.log('Logged out successfully');
        //         history.push('/');
        //     })
        //     .catch((error) => {
        //         if (error.response && error.response.status === 401) {
        //             alert("Session expired. Please log in again.");
        //             setSpLoggedIn(false);
        //             setClient(true);
        //             localStorage.removeItem("token");
        //             localStorage.removeItem("spsalonId");
        //             localStorage.removeItem("owner");
        //             console.log('Session expired, logged out');
        //             history.push('/');
        //         }
        //     });
    };

    // const handleSprovider = () => {
    //     setShowServiceProvider(true);
    // };




    //*** Razorpay */
    const baseUrl = (process.env.REACT_APP_SERVER) ?
        `https://salonserver-k8m8.onrender.com`
        : `http://localhost:3001`;

    const handlePayment = async () => {
        setIsProcessing(true);
        // console.log('owner in payment', owner);
        const amount = 500; // Amount in INR
        const jwt = localStorage.getItem('token');
        try {
            const response = await axios.post(`${baseUrl}/api/v1/payments`,
                { salon_owner_id: owner.id, amount, email: owner.email, name: owner.first_name + ' ' + owner.last_name, mobile: owner.mobile },
                { headers: { "Authorization": `${jwt}` } }
            );
            const { order_id, key, currency } = response.data;
            // console.log('resp', response)

            const options = {
                key,
                amount: amount * 100,
                currency,
                name: owner.name,
                description: "Monthly Subscription Payment",
                order_id,

                handler: async function (response) {
                    try {
                        alert(`Payment successful! Payment ID: ${response.razorpay_payment_id}`);
                        alert('Please wait for screen to refresh or refresh the screen yourself.');
                        setPaymentStatus('success');
                        const paymentUpdateResponse = await axios.post(`${baseUrl}/api/v1/payments/update_payment_status`, {
                            order_id: order_id,
                            payment_id: response.razorpay_payment_id
                        },
                            {
                                headers: {
                                    "Content-Type": "application/json",
                                    "Accept": "application/json",
                                    "Authorization": `${jwt}`
                                }
                            });
                        const subRes = await axios.get(
                            paymentsUrl,
                            { headers: { Authorization: jwt } }
                        );
                        console.log('Payment updated:', subRes.data);

                        // ── SUCCESS ACTIONS ──
                        setSubscription(subRes.data);  // ← this fixes Invalid Date
                        setPaymentStatus("success");  // Set subscription to true
                        getSubscription();
                        setTabValue(5);
                        setIsProcessing(false);// Navigate to tab 5

                    } catch (updateError) {
                        console.error("Payment status update failed:", updateError);
                        setPaymentStatus('failed');
                        setTabValue(5); // Still go to tab 5 even if update fails (safety)
                        setIsProcessing(false);
                    }
                },

                prefill: {
                    name: owner.name,
                    email: owner.email,
                    contact: owner.mobile
                },
                theme: {
                    color: "#3399cc"
                }
            };

            const rzp1 = new window.Razorpay(options);
            rzp1.on('payment.failed', function () {
                setPaymentStatus('failed');
                setTabValue(5);
                setIsProcessing(false);
            });
            rzp1.open();

        } catch (error) {
            console.error("Payment initiation failed:", error);
            setPaymentStatus('failed');
            setTabValue(5);
        } finally {
            setIsProcessing(false);
        }
    };
    /*  Razorpay end */




    const handleSalon = () => {
        // console.log('setShowSalon true');
        setShowSalon(true);
    };


    const drawerContent = (
        <Box sx={{ width: 250, p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <GradientTypography variant="h6">
                    Salon Dashboard
                </GradientTypography>
                <IconButton onClick={handleDrawerToggle}>
                    <CloseIcon sx={{ color: 'primary.main' }} />
                </IconButton>
            </Box>
            <Divider sx={{ bgcolor: 'primary.main', height: 2 }} />
            <Tabs orientation="vertical" value={tabValue} onChange={handleTabChange} sx={{ mt: 2 }}>
                <StyledTab label="Analytics" />
                <StyledTab label="Salon Details" />
                <StyledTab label="Services" />
                <StyledTab label="Appointments" />
                <StyledTab label="Profile" />
                <StyledTab label="Payment" />
            </Tabs>
        </Box>
    );

    const formatApptTime = (isoTime) => {
        if (!isoTime) return '';
        const date = new Date(isoTime);
        if (isNaN(date.getTime())) return '';
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${hours}:${minutes}`;
    };

    const renderAnalytics = () => (
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
            {/* {console.log('spSalonId in renderAnalytics:', spSalonId)} */}
            {(!(spSalonId > 0)) ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2 }}>
                    <StyledButton onClick={handleSalon}>
                        Setup Your Salon
                    </StyledButton>
                </Box>
            ) : null
            }
            {/* {console.log('owner', owner)} */}
            {/* {console.log("Dashboard re-render")} */}


            {showSalon && <Salons spId={owner.id} setShowSalon={setShowSalon} />}
            <GradientTypography variant="h5" gutterBottom>
                Analytics Overview
            </GradientTypography>
            <Typography variant="h6" gutterBottom>
                This is sample data. You will see actual data once the operations start
            </Typography>
            <Grid container spacing={{ xs: 2, sm: 3 }}>
                <Grid item xs={12} sm={4}>
                    <StyledCard>
                        <CardContent>
                            <Typography variant="h6" color="primary">Total Appointments</Typography>
                            <Typography variant="h4">{analytics.totalAppointments}</Typography>
                        </CardContent>
                    </StyledCard>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <StyledCard>
                        <CardContent>
                            <Typography variant="h6" color="primary">Revenue This Month</Typography>
                            <Typography variant="h4">₹{analytics.revenueThisMonth}</Typography>
                        </CardContent>
                    </StyledCard>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <StyledCard>
                        <CardContent>
                            <Typography variant="h6" color="primary">Most Popular Service</Typography>
                            <Typography variant="h4">{analytics.mostPopularService}</Typography>
                        </CardContent>
                    </StyledCard>
                </Grid>
            </Grid>
            <Typography variant="h6" gutterBottom>
                We welcome your suggestions to improve this platform. Please mail to krs30018@gmail.com
            </Typography>
        </Box >
    );

    const renderSalonDetails = () => (
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
            {/* {console.log('spSalonId in Salon:', spSalonId)} */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <GradientTypography variant="h5">
                    Salon Details
                </GradientTypography>
                <StyledButton startIcon={<EditIcon />} onClick={() => setSalonFormOpen(true)}>
                    Edit Salon
                </StyledButton>
            </Box>
            {salonFormOpen ? (
                <Box component="form" onSubmit={updateSalonDetails} sx={{ mt: 3 }}>
                    <Grid container spacing={{ xs: 1, sm: 2 }}>
                        <Grid item xs={12}>
                            <StyledTextField fullWidth label="Name of the Salon" name="name" value={salonForm?.name || ''} onChange={handleSalonFormChange} required />
                        </Grid>
                        <Grid item xs={12}>
                            <StyledTextField fullWidth label="Salon Email" name="email" type="email" value={salonForm?.email || ''} onChange={handleSalonFormChange} required />
                        </Grid>
                        <Grid item xs={12}>
                            <StyledTextField fullWidth label="Salon Address 1" name="address1" value={salonForm?.address1 || ''} onChange={handleSalonFormChange} required />
                        </Grid>
                        <Grid item xs={12}>
                            <StyledTextField fullWidth label="Salon Address 2" name="address2" value={salonForm?.address2 || ''} onChange={handleSalonFormChange} required />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <StyledTextField fullWidth label="Pin Code" name="pincode" type="number" value={salonForm?.pincode || ''} onChange={handleSalonFormChange} required />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <StyledTextField fullWidth label="City" name="city" value={salonForm?.city || ''} onChange={handleSalonFormChange} required />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth required>
                                <InputLabel>Salon Type</InputLabel>
                                <Select
                                    name="salon_type"
                                    value={salonForm?.salon_type || ''}
                                    onChange={handleSalonFormChange}
                                >
                                    <MenuItem value="">Select</MenuItem>
                                    <MenuItem value="Women Only">Women Only</MenuItem>
                                    <MenuItem value="Men Only">Men Only</MenuItem>
                                    <MenuItem value="Unisex">Unisex</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <StyledTextField
                                fullWidth
                                label="Landline"
                                name="landline"
                                type="tel"
                                value={salonForm?.landline || ''}
                                onChange={handleSalonFormChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <StyledTextField fullWidth label="Mobile" name="mobile" type="tel" value={salonForm?.mobile || ''} onChange={handleSalonFormChange} required />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <StyledTextField
                                fullWidth
                                label="GST Number"
                                name="gst_number"
                                value={salonForm?.gst_number || ''}
                                onChange={handleSalonFormChange}
                                inputProps={{
                                    pattern: '^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$',
                                    title: 'Enter valid GSTIN format (e.g. 22AAAAA0000A1Z5)',
                                    maxLength: 15,
                                    style: { textTransform: 'uppercase' }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <StyledTextField
                                fullWidth
                                label="PAN Number"
                                name="pan_number"
                                value={salonForm?.pan_number || ''}
                                onChange={handleSalonFormChange}
                                inputProps={{
                                    pattern: '^[A-Z]{5}[0-9]{4}[A-Z]{1}$',
                                    title: 'Enter valid PAN format (e.g. ABCDE1234F)',
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <StyledTextField fullWidth label="No. of Chairs" name="chairs" type="number" value={salonForm?.chairs || ''} onChange={handleSalonFormChange} required />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Tooltip title="Enter time in 24 hour format" placement="right" arrow>
                                <StyledTextField
                                    fullWidth
                                    label="Opens"
                                    name="opens"
                                    type="time"
                                    value={formatTimeForInput(salonForm?.opens) || ''}
                                    onChange={handleSalonFormChange}
                                    required
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Tooltip>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Tooltip title="Enter time in 24 hour format" placement="right" arrow>
                                <StyledTextField
                                    fullWidth
                                    label="Closes"
                                    name="closes"
                                    type="time"
                                    value={formatTimeForInput(salonForm?.closes) || ''}
                                    onChange={handleSalonFormChange}
                                    required
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Tooltip>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth required>
                                <InputLabel>Weekly Holiday</InputLabel>
                                <Select name="weekday" value={salonForm?.weekday || ''} onChange={handleSalonFormChange}>
                                    <MenuItem value="">Select</MenuItem>
                                    <MenuItem value={1}>Sunday</MenuItem>
                                    <MenuItem value={2}>Monday</MenuItem>
                                    <MenuItem value={3}>Tuesday</MenuItem>
                                    <MenuItem value={4}>Wednesday</MenuItem>
                                    <MenuItem value={5}>Thursday</MenuItem>
                                    <MenuItem value={6}>Friday</MenuItem>
                                    <MenuItem value={7}>Saturday</MenuItem>
                                </Select>
                            </FormControl>
                            <Grid item xs={12}>
                                <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                                    📍 Location Upload
                                </label>
                                <LocationPicker
                                    latitude={latitude}
                                    longitude={longitude}
                                    setLatitude={setLatitude}
                                    setLongitude={setLongitude}
                                    isLoaded={isLoaded}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <label style={{ display: 'block', marginTop: '1rem', marginBottom: '0.5rem' }}>
                                    🖼️ Image Upload
                                </label>
                                <input
                                    type="file"
                                    accept="image/*"
                                    multiple={false}
                                    onChange={onImageChange}
                                    style={{ display: 'block' }}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                        <StyledButton type="submit">Save</StyledButton>
                        <Button variant="outlined" onClick={() => setSalonFormOpen(false)}>Cancel</Button>
                    </Box>
                </Box>
            ) : (
                <StyledCard>
                    <CardContent>
                        <Typography variant="h6" color="primary">{salonData?.name || 'No Name'}</Typography>
                        <Typography>Email: {salonData?.email}</Typography>
                        <Typography>Address: {salonData?.address1}, </Typography>
                        <Typography sx={{ paddingLeft: '4.2rem' }}>{salonData?.address2}, </Typography>
                        <Typography sx={{ paddingLeft: '4.2rem' }}>{salonData?.city}, </Typography>
                        <Typography sx={{ paddingLeft: '4.2rem' }}>{salonData?.pincode}, </Typography>
                        <Typography>Mobile: {salonData?.mobile}</Typography>
                        <Typography>Landline: {salonData?.landline}</Typography>
                        <Typography>Chairs: {salonData?.chairs}</Typography>
                        <Typography>Operating Hours: {formatTimeForInput(salonData?.opens)} - {formatTimeForInput(salonData?.closes)}</Typography>
                        <Typography>Weekly Holiday: {['', 'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][salonData?.weekday]}</Typography>
                        <Typography>GST No.: {salonData?.gstin}</Typography>
                        <Typography>PAN: {salonData?.pan}</Typography>
                    </CardContent>
                </StyledCard>
            )}
        </Box>
    );

    const renderServices = () => (
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
            {/* {console.log('spSalonId in renderService:', spSalonId)} */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <GradientTypography variant="h5">
                    Services
                </GradientTypography>
                <StyledButton startIcon={<AddIcon />} onClick={() => setServiceFormOpen(true)}>
                    Add Service
                </StyledButton>
            </Box>
            {serviceFormOpen ? (
                <Box component="form" onSubmit={handleServiceSubmit} sx={{ mt: 3, mb: 3 }}>
                    <Grid container spacing={{ xs: 1, sm: 2 }}>
                        <Grid item xs={12}>
                            <StyledTextField fullWidth label="Service Name" name="stype" value={serviceForm.stype} onChange={handleServiceFormChange} required />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <StyledTextField fullWidth label="Price (₹)" name="sprice" type="number" value={serviceForm.sprice} onChange={handleServiceFormChange} required />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <StyledTextField fullWidth label="Duration" name="sduration" value={serviceForm.sduration} onChange={handleServiceFormChange} required />
                        </Grid>
                    </Grid>
                    <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                        <StyledButton type="submit">{serviceForm.id ? 'Update' : 'Add'}</StyledButton>
                        <Button variant="outlined" onClick={() => { setServiceFormOpen(false); setServiceForm({ id: null, stype: '', sprice: '', sduration: '' }); }}>
                            Cancel
                        </Button>
                    </Box>
                </Box>
            ) : null}
            <TableContainer component={Paper} sx={{ mt: 2, borderRadius: 2, boxShadow: 3, overflowX: 'auto' }}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ bgcolor: 'primary.light' }}>
                            <StyledTableCell>Name</StyledTableCell>
                            <StyledTableCell>Price-- (₹)</StyledTableCell>
                            <StyledTableCell>Duration</StyledTableCell>
                            <StyledTableCell>Actions</StyledTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {services.map(service => (
                            <StyledTableRow key={service.id}>
                                <TableCell>{service.stype}</TableCell>
                                <TableCell>{service.sprice}</TableCell>
                                <TableCell>{service.sduration}</TableCell>
                                <TableCell>
                                    <IconButton onClick={() => handleEditService(service)}><EditIcon color="black" /></IconButton>
                                    <IconButton onClick={() => handleDeleteService(service.id)}><DeleteIcon color="error" /></IconButton>
                                </TableCell>
                            </StyledTableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );

    const renderAppointments = () => {
        const handleApptTabChange = (event, newValue) => {
            setApptTabValue(newValue);
        };

        const currentDate = new Date();

        return (
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
                {/* {console.log('spSalonId in renderAppts:', spSalonId)} */}
                <GradientTypography variant="h5" gutterBottom>
                    Appointments
                </GradientTypography>
                <Tabs
                    value={apptTabValue}
                    onChange={handleApptTabChange}
                    sx={{
                        bgcolor: 'primary.main',
                        borderRadius: 1,
                        mb: 2,
                        '& .MuiTab-root': {
                            color: 'white',
                            '&.Mui-selected': {
                                color: 'white',
                                backgroundColor: 'primary.light',
                            },
                            '&:hover': {
                                backgroundColor: 'primary.dark',
                            },
                            [theme.breakpoints.down('sm')]: {
                                fontSize: '0.9rem',
                                padding: theme.spacing(1),
                            },
                        },
                    }}
                    centered
                >
                    <Tab label="Upcoming" />
                    <Tab label="Past" />
                </Tabs>
                <TableContainer component={Paper} sx={{ mt: 2, borderRadius: 2, boxShadow: 3, overflowX: 'auto' }}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: 'primary.light' }}>
                                <StyledTableCell>Customer</StyledTableCell>
                                <StyledTableCell>Mobile</StyledTableCell>
                                <StyledTableCell>Service</StyledTableCell>
                                <StyledTableCell>Date</StyledTableCell>
                                <StyledTableCell>Time</StyledTableCell>
                                <StyledTableCell>Status</StyledTableCell>
                                <StyledTableCell>Rating</StyledTableCell>
                                <StyledTableCell>Feedback</StyledTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {appointments.length > 0 ? (
                                appointments
                                    .filter((a) => {
                                        const appointmentDate = new Date(a.apptdate);
                                        const isUpcoming = appointmentDate > currentDate;
                                        return isUpcoming === (apptTabValue === 0);
                                    })
                                    .map((appointment) => (
                                        <StyledTableRow key={appointment.id}>
                                            <TableCell>{appointment.user.first_name + ' ' + appointment.user.last_name}</TableCell>
                                            <TableCell>{appointment.user.mobile}</TableCell>
                                            <TableCell>{appointment.service}</TableCell>
                                            <TableCell>{new Date(appointment.apptdate).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}</TableCell>
                                            <TableCell>{formatApptTime(appointment.appttime)}</TableCell>
                                            <TableCell>{appointment.status}</TableCell>
                                            <TableCell>{appointment.rating || 'N/A'}</TableCell>
                                            <TableCell>{appointment.feedback || 'N/A'}</TableCell>
                                        </StyledTableRow>
                                    ))
                            ) : (
                                <StyledTableRow>
                                    <TableCell colSpan={8}>No appointments available</TableCell>
                                </StyledTableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        );
    };

    const renderProfile = () => (
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
            {/* {console.log('spSalonId in renderProfile:', spSalonId, profileForm)} */}
            <GradientTypography variant="h5" gutterBottom>
                Profile
            </GradientTypography>
            <Box component="form" onSubmit={handleProfileSubmit} sx={{ maxWidth: { xs: '100%', sm: 400 } }}>
                <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56, mb: 2 }}>
                    <SpaOutlinedIcon />
                </Avatar>
                <StyledTextField
                    fullWidth
                    label="First Name"
                    name="first_name"
                    value={profileForm?.first_name || ''}
                    onChange={handleProfileFormChange}
                    sx={{ mb: 2 }}
                    required
                />
                <StyledTextField
                    fullWidth
                    label="Last Name"
                    name="last_name"
                    value={profileForm?.last_name || ''}
                    onChange={handleProfileFormChange}
                    sx={{ mb: 2 }}
                    required
                />
                <StyledTextField
                    fullWidth
                    label="Email can't be changed"
                    name="email"
                    type="email"
                    value={profileForm?.email || ''}
                    onChange={handleProfileFormChange}
                    sx={{ mb: 2 }}
                    InputProps={{ readOnly: true }}
                />
                <StyledTextField
                    fullWidth
                    label="Mobile"
                    name="mobile"
                    type="tel"
                    value={profileForm?.mobile || ''}
                    onChange={handleProfileFormChange}
                    sx={{ mb: 2 }}
                    required
                />
                <StyledTextField
                    fullWidth
                    label="Date of Birth"
                    name="date_of_birth"
                    type="date"
                    value={profileForm?.date_of_birth || ''}
                    onChange={handleProfileFormChange}
                    sx={{ mb: 2 }}
                    InputLabelProps={{ shrink: true }}
                    required
                />
                <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Gender</InputLabel>
                    <Select
                        name="gender"
                        value={profileForm?.gender || ''}
                        onChange={handleProfileFormChange}
                        required
                    >
                        <MenuItem value="male">Male</MenuItem>
                        <MenuItem value="female">Female</MenuItem>
                    </Select>
                </FormControl>
                <StyledButton type="submit">Update Profile</StyledButton>
            </Box>
        </Box>
    );




    const renderPayment = () => {
        const today = new Date();

        // ---------- STATE 1: NOT STARTED ----------
        if (!subscription) {
            return (
                <Box sx={{ p: { xs: 2, sm: 3 } }}>
                    <Typography variant="h5" fontWeight="bold" gutterBottom>
                        Subscription Details : Not Started
                    </Typography>
                    <Typography variant="h6" color="warning.main" gutterBottom>
                        🚀 Start your GroomWell subscription
                    </Typography>
                    <Typography sx={{ mb: 3 }}>
                        Activate your subscription to receive bookings and manage your salon.
                    </Typography>
                    {/* STATUS */}
                    <Typography
                        variant="h6"
                        sx={{
                            // color: isExpired ? "error.main" : "success.main",
                            fontWeight: "bold",
                            mb: 2,
                        }}
                    >
                    </Typography>

                    <Container sx={{ mt: 3 }}>
                        <hr />

                        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                            <Typography>Salon Owner</Typography>
                            <Typography>
                                {owner.first_name} {owner.last_name}
                            </Typography>
                        </Box>

                        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                            <Typography>Email</Typography>
                            <Typography>{owner.email}</Typography>
                        </Box>

                        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                            <Typography>Plan</Typography>
                            <Typography>GroomWell Salon Subscription</Typography>
                        </Box>

                        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                            <Typography>Amount</Typography>
                            <Typography>₹ 500</Typography>
                        </Box>

                        <hr />
                        <Box sx={{ textAlign: "center" }}>
                            <Button
                                variant="contained"
                                color="success"
                                size="large"
                                onClick={handlePayment}
                                disabled={isProcessing}
                                startIcon={
                                    isProcessing ? <CircularProgress size={20} color="inherit" /> : null
                                }
                            >
                                {isProcessing ? "Processing..." : "Activate Subscription"}
                            </Button>
                        </Box>

                    </Container>
                </Box>
            );
        }

        const endDate = new Date(subscription.end_date);
        const isExpired = endDate < today;
        console.log('Subscription end date:', endDate, 'Is expired:', isExpired);
        // ---------- STATE 2 & 3: STARTED ----------
        return (
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                    Subscription Details
                </Typography>

                {/* STATUS */}
                <Typography
                    variant="h6"
                    sx={{
                        color: isExpired ? "error.main" : "success.main",
                        fontWeight: "bold",
                        mb: 2,
                    }}
                >
                    {isExpired
                        ? "❌ Subscription Expired"
                        : `✅ Active till ${endDate.toDateString()}`}
                </Typography>

                <Container sx={{ mt: 3 }}>
                    <hr />

                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                        <Typography>Salon Owner</Typography>
                        <Typography>
                            {owner.first_name} {owner.last_name}
                        </Typography>
                    </Box>

                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                        <Typography>Email</Typography>
                        <Typography>{owner.email}</Typography>
                    </Box>

                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                        <Typography>Plan</Typography>
                        <Typography>GroomWell Salon Subscription</Typography>
                    </Box>

                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                        <Typography>Amount</Typography>
                        <Typography>₹{subscription.amount / 100}</Typography>
                    </Box>

                    <hr />

                    {/* ACTION */}
                    {isExpired ? (
                        <>
                            <Typography sx={{ mt: 2 }} color="error">
                                Your subscription has expired. Renew to continue receiving bookings.
                            </Typography>

                            <Box sx={{ textAlign: "center", mt: 3 }}>
                                <Button
                                    variant="contained"
                                    color="success"
                                    size="large"
                                    onClick={handlePayment}
                                >
                                    Renew Subscription
                                </Button>
                            </Box>
                        </>
                    ) : (
                        <>
                            <Typography sx={{ mt: 2 }} color="success.main">
                                🎉 Your subscription is active.
                            </Typography>
                            <Box sx={{ textAlign: "center", mt: 3 }}>
                                <Button
                                    variant="contained"
                                    color="success"
                                    size="large"
                                    onClick={handlePayment}
                                >
                                    You can Renew here before it expires
                                </Button>
                            </Box>
                        </>

                    )}
                </Container>
            </Box>
        );
    };


    return (
        <ThemeProvider theme={theme}>
            <DashboardContainer>
                <Dialog open={openServiceModal} onClose={() => setOpenServiceModal(false)}>
                    <DialogTitle>Services Setup Required</DialogTitle>
                    <DialogContent>
                        <p>Please set up your salon services before continuing.</p>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            variant="contained"
                            onClick={() => {
                                setOpenServiceModal(false);
                                setTabValue(2);  // Ensure Services tab stays open
                            }}
                        >
                            Go to Services
                        </Button>
                    </DialogActions>
                </Dialog>
                {/* <Dialog open={openPaymentModal} onClose={() => setOpenPaymentModal(false)}>
                    <DialogTitle>Payment Required</DialogTitle>
                    <DialogContent>
                        <p>Please complete your payment to continue.</p>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            variant="contained"
                            onClick={() => {
                                setOpenPaymentModal(false);
                                setTabValue(5);  // Ensure Services tab stays open
                            }}
                        >
                            Go To Payment Menu
                        </Button>
                    </DialogActions>
                </Dialog>  */}

                <HeroSection sx={{
                    backgroundImage: `url(${salonData?.get_image_url || bgUrl})`,
                    backgroundSize: "cover",  // show full image
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "center",
                    minHeight: "300px",          // adjust height as needed
                }}>
                    < Box sx={{ position: 'relative', zIndex: 1 }}>
                        <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
                            Welcome to {salonData?.name || 'Salons Platform'}
                        </Typography>
                        <Typography variant="h5" gutterBottom>ID: {salonData?.id}</Typography>
                        <Typography variant="h6">
                            Manage your salon with ease
                        </Typography>
                    </Box>
                </HeroSection>
                <Box sx={{ display: 'flex' }}>
                    <StyledAppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
                        <Toolbar>
                            <IconButton
                                color="inherit"
                                edge="start"
                                onClick={handleDrawerToggle}
                                sx={{ mr: 2 }}
                            >
                                <MenuIcon />
                            </IconButton>
                            <Box sx={{ flexGrow: 1, textAlign: 'center' }}>
                                <Typography variant="h6" noWrap component="div" sx={{ color: 'white' }}>
                                    Salon Dashboard
                                </Typography>
                            </Box>
                            <StyledButton
                                color="inherit"
                                startIcon={<LogoutIcon />}
                                onClick={handleSpLogout}
                                sx={{ textTransform: 'none', fontWeight: 'bold' }}
                            >
                                Log out
                            </StyledButton>
                        </Toolbar>
                    </StyledAppBar>

                    <StyledDrawer
                        variant="temporary"
                        open={drawerOpen}
                        onClose={handleDrawerToggle}
                        sx={{ width: { sm: 250 }, flexShrink: 0 }}
                    >
                        {drawerContent}
                    </StyledDrawer>
                    <Box component="main" sx={{ flexGrow: 1, p: { xs: 2, sm: 3 }, mt: 8 }}>
                        {tabValue === 0 && renderAnalytics()}
                        {tabValue === 1 && renderSalonDetails()}
                        {tabValue === 2 && renderServices()}
                        {tabValue === 3 && renderAppointments()}
                        {tabValue === 4 && renderProfile()}
                        {tabValue === 5 && renderPayment()}
                    </Box>
                </Box>
                {/* <Box sx={{ px: 2, pt: 2 }}>
                    {!subscription && (
                        <Paper
                            elevation={2}
                            sx={{
                                p: 2,
                                borderRadius: 3,
                                background: "linear-gradient(135deg, rgb(226, 233, 172) 0%, rgb(233, 141, 125) 60%)",
                                border: "1px solid #FED7AA",

                            }}
                        >
                            <Stack
                                direction={{ xs: "column", sm: "row" }}
                                spacing={2}
                                alignItems={{ xs: "flex-start", sm: "center" }}
                                justifyContent="space-between"
                            >
                                <Typography sx={{ fontSize: 15, fontWeight: "bold", color: "#4a148c" }}>
                                    ⚠️ Your subscription is inactive. Complete payment to activate your salon and start receiving bookings.
                                </Typography>

                                <Button
                                    variant="contained"
                                    size="small"
                                    onClick={() => setTabValue(5)}
                                    sx={{
                                        borderRadius: 2,
                                        textTransform: "none",
                                        px: 3
                                    }}
                                >
                                    Go to Payments
                                </Button>
                            </Stack>
                        </Paper>)}
                </Box> */}

            </DashboardContainer>

        </ThemeProvider >

    );
}

export default Dashboard;
